import {
  CaseReducer,
  createAsyncThunk,
  createSlice,
  current,
  PayloadAction,
} from "@reduxjs/toolkit";
import { RequestField, RequestsInitialState } from "../types/RequestsTypes";
import { api } from "../api";
import moment from "moment";

const checkStepValidation = (state: RequestsInitialState, stepId: number) => {
  switch (stepId) {
    case 2: {
      if (
        state.choosenSlot &&
        (state.choosenSlot.equipments.length > 0 ||
          state.proposalFormSettings.layouts.length > 0)
      ) {
        return true;
      }

      return false;
    }
    case 3:
      if (state.choosenSlot && state.choosenSlot.menu) {
        return true;
      }

      return false;

    default:
      return true;
  }
};

export const fetchRequestsSettings = createAsyncThunk<
  Omit<RequestsInitialState, "loading">
>("requests", async (_, thunkAPI) => {
  try {
    const responseRequestsSettings = await thunkAPI.dispatch(
      api.getRequestsSettings.initiate("")
    );

    return {
      requestsSettings: responseRequestsSettings.data?.request_fields,
    } as Omit<RequestsInitialState, "loading">;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const fetchMonthlyAvailability = createAsyncThunk<
  Omit<RequestsInitialState, "loading">,
  { id_room: string | undefined; date: string }
>(
  "bookings",
  /**  @param arg {{ id_room: string, date: string }} */
  async (arg, thunkAPI) => {
    try {
      const proposalFormSettings = await thunkAPI.dispatch(
        api.fetch_availability.initiate({ ...arg })
      );

      return {
        proposalFormSettings: proposalFormSettings.data as any,
      } as Omit<RequestsInitialState, "loading">;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const stepReducer: CaseReducer<RequestsInitialState, PayloadAction<number>> = (
  state,
  action
) => {
  const currentState = current(state);
  state.step = checkStepValidation(currentState, action.payload)
    ? action.payload
    : currentState.step;

  return state;
};

const formSaveReducer: CaseReducer<
  RequestsInitialState,
  PayloadAction<{ step: number; data: any }>
> = (state, action) => {
  const currentState = current(state);

  state.formState[action.payload.step] = action.payload.data;
  state.submitData = { ...currentState.submitData, ...action.payload.data };

  let checkedStep = action.payload.step;

  do {
    checkedStep = checkedStep + 1;
  } while (!checkStepValidation(currentState, checkedStep));

  state.step = checkedStep;
  return state;
};

const saveFormData: CaseReducer<
  RequestsInitialState,
  PayloadAction<{ step: number; data: any }>
> = (state, action) => {
  if (!action.payload.data.choosenSlot) {
    state.formState[action.payload.step] = action.payload.data;
  } else {
    state.choosenSlot = action.payload.data.choosenSlot;
  }

  return state;
};

const inititalFormStateForSteps = {
  1: {
    date: moment().format("YYYY-MM-DD"),
    pax: 0,
    note: "",
    type: 0,
    id_room_template_slot: null,
  },
  2: {
    equipments: [],
    layout: null,
  },
  3: {
    menu: [],
  },
};

let inititalFormState = {};

Object.values(inititalFormStateForSteps).forEach((item) => {
  inititalFormState = { ...inititalFormState, ...item };
});

const requests = createSlice({
  name: "requests",
  initialState: {
    requestsSettings: [] satisfies RequestField[] as RequestField[],
    headers: null,
    loading: false as boolean,
    locale: (navigator && navigator.language.slice(0, 2)) || "en",
    step: 1,
    choosenSlot: null,
    submitData: inititalFormState,
    formState: {
      1: inititalFormStateForSteps[1],
      2: inititalFormStateForSteps[2],
      3: inititalFormStateForSteps[3],
      4: {},
      5: {},
    },
    proposalFormSettings: {},
  } as unknown as RequestsInitialState,
  reducers: {
    setStep: stepReducer,
    setFormState: formSaveReducer,
    setData: saveFormData,
  },
  extraReducers(builder) {
    builder.addCase(fetchRequestsSettings.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchRequestsSettings.fulfilled, (state, action) => {
      state.requestsSettings = action.payload.requestsSettings;
      state.loading = false;
    });
    builder.addCase(fetchMonthlyAvailability.fulfilled, (state, action) => {
      state.proposalFormSettings = action.payload.proposalFormSettings;

      const currentState = current(state);
      const monthFormat = "YYYY-MM-DD";
      const today = moment().format(monthFormat);

      Object.entries(currentState.proposalFormSettings.slots).find(
        ([key, value]) => {
          if (
            moment(key, monthFormat).isSameOrAfter(
              moment(today, monthFormat)
            ) &&
            Array.isArray(value) &&
            value.length > 0
          ) {
            state.submitData.date = key;
            state.formState[1].date = key;
            return true;
          }
          state.submitData.date = today;
          state.formState[1].date = today;
          return false;
        }
      );

      state.submitData.pax = Number(
        action.payload.proposalFormSettings.standing_capacity
      );
      state.formState[1].pax = Number(
        action.payload.proposalFormSettings.standing_capacity
      );
      state.loading = false;
    });
  },
});

export const { setStep, setFormState, setData } = requests.actions;

export default requests.reducer;
